import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { FeatureEnabled } from "components/core/FeatureEnabled";
import { Separator } from "components/core/Seperator";
import {
    ArchiveCard,
    ArchiveItem
} from "components/domain/archive/ArchiveCard";
import { ArchiveHorizontalCard } from "components/domain/archive/ArchiveHorizontalCard";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import { ContentCardSekeleton } from "components/domain/contents/cards/ContentCardSkeleton";
import {
    SectionControls,
    SectionHeader,
    SectionTitle
} from "components/domain/contents/sections";
import { useMixContentQuery } from "graphql/generated";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useWindowSize } from "hooks/useWindowSize";
import { fireEvent } from "lib/ga";
import partition from "lodash/partition";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ComponentProps, ReactNode, Suspense, useMemo } from "react";
import { LIMIT, buildQueryVariables } from "utils/buildQueryVariables";
import { CAMPAIGN_TO_TAGS, ORDERED_MIX_CONTENT_TAG_ENUM } from "utils/enum";

import { ExtensionLink } from "../../core/ExtensionLink";
import { ContentGrid } from "./ContentGrid";
import { HorizontalCard } from "./cards/HorizontalCard";

// Conditionally lazy load PersonalFeedSection based on build target
// Extension builds (output: "export") don't support dynamic imports properly
const PersonalFeedSection =
    process.env.NEXT_PUBLIC_BUILD_TARGET === "extension"
        ? require("components/domain/contents/sections/PersonalFeedSection")
              .PersonalFeedSection
        : dynamic(
              () =>
                  import("components/domain/contents/sections/PersonalFeedSection").then(
                      (mod) => mod.PersonalFeedSection
                  ),
              {
                  loading: () => (
                      <div className="col-[1/span_2] grid grid-cols-2 gap-6">
                          <ContentCardSekeleton />
                          <ContentCardSekeleton />
                      </div>
                  ),
                  ssr: false // Load only on client side to improve initial SSR performance
              }
          );

// Conditionally lazy load VotingCard based on build target
// Extension builds (output: "export") don't support dynamic imports properly
const VotingCard =
    process.env.NEXT_PUBLIC_BUILD_TARGET === "extension"
        ? require("components/domain/contents/cards/VotingCard").VotingCard
        : dynamic(
              () =>
                  import("components/domain/contents/cards/VotingCard").then(
                      (mod) => mod.VotingCard
                  ),
              {
                  loading: () => <ContentCardSekeleton />,
                  ssr: false // Load only on client side
              }
          );

export const MixedContentGrid = ({
    contents,
    loading,
    showSkeleton = false,
    empty,
    archives = [],
    archiveTagEnums = [],
    archivesLoading = false,
    isGuest = false
}: ComponentProps<typeof ContentGrid> & {
    archives?: ArchiveItem[];
    archiveTagEnums?: Array<{ value: string; comment?: string | null }>;
    archivesLoading?: boolean;
    isGuest?: boolean;
}) => {
    const router = useRouter();
    const { width } = useWindowSize();
    const { isFeatureEnabled } = useFeatureFlags();
    const isSmallScreen = typeof width === "number" ? width <= 768 : false;
    const isDesktopLayout = !isSmallScreen;
    const isLargeScreen = typeof width === "number" ? width > 1200 : false;
    const isExtraLargeScreen = typeof width === "number" ? width > 1563 : false;
    const showVotingCard = isFeatureEnabled("HOME_PAGE_VOTING_CARD");
    const showPersonalFeed = isFeatureEnabled("HOME_PAGE_PERSONAL_FEED");

    const tags = useMemo(() => {
        const { tag } = router.query;

        // Handle campaign tags (trial_1, trial, etc.)
        if (typeof tag === "string") {
            const campaignTags = CAMPAIGN_TO_TAGS[tag as string] ?? null;
            if (campaignTags) return campaignTags;
        }

        const tagFromQuery = ORDERED_MIX_CONTENT_TAG_ENUM.find(
            (enumTag) => enumTag === tag
        );

        return tagFromQuery ? [tagFromQuery] : null;
    }, [router]);

    const { data } = useMixContentQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables: {
            ...buildQueryVariables("original", tags, LIMIT.original),
            limit: LIMIT.original,
            offset: 0
        }
    });

    const _originalContents = useMemo(
        () => (data ? data.mix_content : []),
        [data]
    );

    const [originalContents, recentContents] = useMemo(
        () =>
            partition(
                _originalContents.slice(0, 2).concat(contents),
                (content) => content.isoriginal
            ),
        [contents, _originalContents]
    );

    const showEmpty = useMemo(
        () =>
            !loading &&
            !archivesLoading &&
            recentContents.length === 0 &&
            archives.length === 0,
        [loading, archivesLoading, recentContents.length, archives.length]
    );

    const getContentType = (content: Mix_ContentFragment) =>
        content.isoriginal
            ? content.released
                ? ("original" as const)
                : ("upcoming" as const)
            : ("curation" as const);

    const getContentClickHandler = (content: Mix_ContentFragment) => () => {
        const params = {
            content_section: content.isoriginal
                ? ("original-main" as const)
                : ("recent" as const),
            content_url: content.originalurl,
            content_isoriginal: content.isoriginal,
            content_released: content.released
        };

        fireEvent({
            event: "content_click",
            params: {
                content_id: content.id,
                content_title: content.title,
                ...params
            }
        });
    };

    const handleArchiveClick = (archive: ArchiveItem) => () => {
        if (typeof window !== "undefined") {
            window.open(archive.view_url, "_blank");
        }
    };

    const renderWithSeparators = (items: ReactNode[]) =>
        items.flatMap((item, index) => {
            if (!item) {
                return [];
            }

            const elements = [item];

            if (index < items.length - 1) {
                elements.push(
                    <Separator
                        key={`separator-${index}`}
                        className="my-[29px] -ml-4 w-screen"
                    />
                );
            }

            return elements;
        });

    const archiveNodes = archives.map((archive) =>
        isDesktopLayout ? (
            <ArchiveCard
                key={`archive-${archive.id}`}
                archive={archive}
                tagEnums={archiveTagEnums}
                onClick={handleArchiveClick(archive)}
                isGuest={isGuest}
            />
        ) : (
            <ArchiveHorizontalCard
                key={`archive-${archive.id}`}
                archive={archive}
                tagEnums={archiveTagEnums}
                onClick={handleArchiveClick(archive)}
                isGuest={isGuest}
            />
        )
    );

    const recentNodes = recentContents.map((content, index) =>
        isDesktopLayout ? (
            <ContentCard
                key={content.id}
                type={getContentType(content)}
                content={content}
                onClick={getContentClickHandler(content)}
                priority={index < 4} // First 4 items are above-the-fold
            />
        ) : (
            <HorizontalCard
                key={content.id}
                content={content}
                onClick={getContentClickHandler(content)}
                priority={index < 4} // First 4 items are above-the-fold on mobile too
            />
        )
    );

    return (
        <>
            {/* Main grid for PersonalFeedSection and Original Content */}
            {isLargeScreen && tags === null ? (
                <>
                    <div
                        className={cn(
                            "animate-slidein h-full w-full",
                            showEmpty
                                ? "flex items-center justify-center"
                                : isDesktopLayout
                                  ? `grid grid-flow-dense
                                    grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                                    gap-x-6 gap-y-16`
                                  : "flex flex-col"
                        )}
                    >
                        {contents.length > 0 ? (
                            <>
                                {/* PersonalFeedSection - First 2 slots (when enabled) */}
                                <FeatureEnabled featureFlag="HOME_PAGE_PERSONAL_FEED">
                                    <PersonalFeedSection
                                        className={cn(
                                            "relative grid gap-x-6 gap-y-6",
                                            "col-[1/span_2] lg:row-[1]",
                                            `col-1 col-start-1 col-end-3
                                                grid-cols-2`
                                        )}
                                        contentSection="personal-feed"
                                        tags={tags}
                                    />
                                </FeatureEnabled>
                                {/* 콘텐츠 큐레이션 Section - First 2 slots (when PersonalFeed is disabled) */}
                                {!showPersonalFeed && (
                                    <div
                                        className={cn(
                                            "relative grid gap-x-6 gap-y-16",
                                            "col-[1/span_2] lg:row-[1]",
                                            "max-[1550px]:col-[1/span_2]",
                                            "grid-cols-2"
                                        )}
                                    >
                                        <SectionHeader
                                            className="absolute -top-12 flex
                                                w-full items-center
                                                justify-between"
                                        >
                                            <SectionTitle>
                                                콘텐츠 큐레이션
                                            </SectionTitle>
                                        </SectionHeader>
                                        {[
                                            ...archiveNodes,
                                            ...recentNodes
                                        ].slice(0, 2)}
                                    </div>
                                )}
                                {/* Original Content - Next 2 slots */}
                                <div
                                    className={cn(
                                        "relative grid gap-x-6 gap-y-16",
                                        "col-[3/span_2] lg:row-[1]",
                                        "max-[1550px]:col-[3]",
                                        isExtraLargeScreen
                                            ? "grid-cols-2"
                                            : "grid-cols-1"
                                    )}
                                >
                                    <SectionHeader
                                        className="absolute -top-12 flex w-full
                                            items-center justify-between"
                                    >
                                        <SectionTitle>
                                            오리지널 콘텐츠
                                        </SectionTitle>
                                        <SectionControls>
                                            <ExtensionLink href="/original">
                                                <Button
                                                    size="small"
                                                    color="grey"
                                                    variant="text"
                                                    endIcon="ChevronRight"
                                                >
                                                    더보기
                                                </Button>
                                            </ExtensionLink>
                                        </SectionControls>
                                    </SectionHeader>
                                    {/* Voting Card */}
                                    <FeatureEnabled
                                        featureFlag="HOME_PAGE_VOTING_CARD"
                                        fallback={
                                            originalContents.length > 0 &&
                                            originalContents[0] ? (
                                                <ContentCard
                                                    key={`fallback-${originalContents[0].id}`}
                                                    type={getContentType(
                                                        originalContents[0]
                                                    )}
                                                    content={
                                                        originalContents[0]
                                                    }
                                                    onClick={getContentClickHandler(
                                                        originalContents[0]
                                                    )}
                                                    priority={true} // Above-the-fold original content
                                                />
                                            ) : null
                                        }
                                    >
                                        <VotingCard
                                            className={
                                                isExtraLargeScreen &&
                                                originalContents.length > 0 &&
                                                originalContents[0]
                                                    ? "aspect-[1.067]"
                                                    : "h-fit"
                                            }
                                        />
                                    </FeatureEnabled>
                                    {/* Show second original content card */}
                                    {isExtraLargeScreen &&
                                        originalContents.length > 0 &&
                                        (showVotingCard
                                            ? originalContents[0] && (
                                                  <ContentCard
                                                      key={
                                                          originalContents[0].id
                                                      }
                                                      type={getContentType(
                                                          originalContents[0]
                                                      )}
                                                      content={
                                                          originalContents[0]
                                                      }
                                                      onClick={getContentClickHandler(
                                                          originalContents[0]
                                                      )}
                                                      priority={true} // Above-the-fold original content
                                                  />
                                              )
                                            : originalContents[1] && (
                                                  <ContentCard
                                                      key={
                                                          originalContents[1].id
                                                      }
                                                      type={getContentType(
                                                          originalContents[1]
                                                      )}
                                                      content={
                                                          originalContents[1]
                                                      }
                                                      onClick={getContentClickHandler(
                                                          originalContents[1]
                                                      )}
                                                      priority={true} // Above-the-fold original content
                                                  />
                                              ))}
                                </div>
                            </>
                        ) : loading ? (
                            showSkeleton && (
                                <>
                                    <ContentCardSekeleton />
                                    <ContentCardSekeleton />
                                    <ContentCardSekeleton />
                                    <ContentCardSekeleton />
                                </>
                            )
                        ) : (
                            empty
                        )}
                    </div>

                    {/* Separate Content Curation Section - Only when PersonalFeed is ON */}
                    {contents.length > 0 && showPersonalFeed && (
                        <div className="relative mt-[4rem] w-full">
                            <SectionHeader
                                className="absolute -top-12 flex w-full
                                    items-center justify-between"
                            >
                                <SectionTitle>콘텐츠 큐레이션</SectionTitle>
                            </SectionHeader>
                            <div
                                className={cn(
                                    "grid grid-flow-dense",
                                    `grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                                        gap-x-6`,
                                    // "gap-y-16"
                                    "gap-y-6"
                                )}
                            >
                                {[...archiveNodes, ...recentNodes]}
                            </div>
                        </div>
                    )}
                    {/* Remaining Content Curation Items - Only when PersonalFeed is OFF */}
                    {contents.length > 0 && !showPersonalFeed && (
                        <div className="relative w-full">
                            <div
                                className={cn(
                                    "grid grid-flow-dense",
                                    `grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                                        gap-x-6`,
                                    "gap-y-6"
                                )}
                            >
                                {[...archiveNodes, ...recentNodes].slice(2)}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="relative w-full">
                        <SectionHeader
                            className="absolute -top-12 flex w-full items-center
                                justify-between"
                        >
                            <SectionTitle>콘텐츠 큐레이션</SectionTitle>
                        </SectionHeader>
                        <div
                            className={cn(
                                "animate-slidein h-full w-full",
                                showEmpty
                                    ? "flex items-center justify-center"
                                    : isDesktopLayout
                                      ? `// grid grid-flow-dense
                                        grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                                        gap-x-6 gap-y-6`
                                      : "flex flex-col gap-6"
                            )}
                        >
                            {contents.length > 0
                                ? isDesktopLayout
                                    ? [...archiveNodes, ...recentNodes]
                                    : renderWithSeparators([
                                          ...archiveNodes,
                                          ...recentNodes
                                      ])
                                : loading
                                  ? showSkeleton && (
                                        <>
                                            <ContentCardSekeleton />
                                            <ContentCardSekeleton />
                                            <ContentCardSekeleton />
                                            <ContentCardSekeleton />
                                            <ContentCardSekeleton />
                                            <ContentCardSekeleton />
                                        </>
                                    )
                                  : empty}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
